import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import CheckIcon from '@mui/icons-material/Check'
import { SidePaneChat } from '../../../../agent'

import type { ChatMessage } from '../../../../hooks/useDashboardInsightsChat'
import { toSidePaneMessages } from '../../../chat/adapters'
import useTranscribeEndpoint from '../../../../hooks/useTranscribeEndpoint'
import useDictationAccess from '../../../../hooks/useDictationAccess'
import useAccount from '../../../../hooks/useAccount'

export interface ScopedAgentChatDialogProps {
  open: boolean
  onClose: () => void
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  onSend: (input: string) => void | Promise<void>
  /** Latest ```instructions proposal in the conversation (null before the
   * assistant proposes one) — enables "Use these instructions". */
  proposedInstructions: string | null
  /** Accept the proposal: the caller writes it into the wizard's Step 2
   * instructions field and closes the dialog. */
  onUseInstructions: (instructions: string) => void
}

/** Roles worth showing as bubbles — the seeded system prompt and the context
 * user-message the backend builds the conversation from stay hidden, exactly
 * like the #974 insights pane's ChatDrawer does by slicing them off. Here the
 * first two messages of the conversation are always [system, context]. */
function visibleMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.slice(2).filter(m => m.role === 'assistant' || m.role === 'user')
}

/**
 * "Help me write this" (#970 Part A, Step 2, Path B — built under #1048).
 * D4 resolved as direct-LLM (Adam, 2026-08-14): a real conversation against
 * POST /dashboards/:customer/genai-instructions/{initial,chat} — the #974
 * insights-pane pattern, not a sandboxed agent session. The assistant
 * interviews the user and proposes instructions in a tagged fence; "Use these
 * instructions" writes the latest proposal into the wizard's Step 2 field.
 *
 * Presentational only (page → hook → component): all networking lives in
 * useGenAIInstructionsChat, instantiated by WizardStep2Instructions. The
 * conversation is SidePaneChat, the same rows and composer as every agent
 * drawer; dictation rides its composer's mic.
 */
export const ScopedAgentChatDialog: React.FC<ScopedAgentChatDialogProps> = ({
  open,
  onClose,
  messages,
  isLoading,
  error,
  onSend,
  proposedInstructions,
  onUseInstructions,
}) => {
  const shown = visibleMessages(messages)
  const auth = axios.defaults.headers.common['Authorization']
  const transcribeEndpoint = useTranscribeEndpoint()
  // Dictation needs agent access and budget (audit C23); this pane opens below it.
  const dictation = useDictationAccess(useAccount().customer?.name)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyIcon color="info" />
        Help me write this
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: 480 }}>
        <SidePaneChat
          messages={toSidePaneMessages(shown)}
          loading={isLoading}
          loadingTitle={shown.length === 0 ? 'Starting the conversation…' : undefined}
          error={error}
          onSend={(text) => onSend(text)}
          placeholder="Describe what you want the analysis to cover…"
          transcribeEndpoint={transcribeEndpoint}
          dictationDisabledReason={dictation.reason}
          authHeader={typeof auth === 'string' ? auth : undefined}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Tooltip title={proposedInstructions ? '' : 'The assistant hasn’t proposed instructions yet — ask it to write them when you’re ready'}>
          <span>
            <Button
              variant="contained"
              color="info"
              startIcon={<CheckIcon />}
              disabled={!proposedInstructions}
              onClick={() => proposedInstructions && onUseInstructions(proposedInstructions)}
            >
              Use these instructions
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  )
}

export default ScopedAgentChatDialog
